import { createFileRoute, Link, Outlet, useMatch } from '@tanstack/react-router'
import { MailIcon, AlertTriangleIcon, LayoutGridIcon } from 'lucide-react'
import { AnimatedTabs } from '@/components/custom/animated-tabs'
import { PanelBlockPending } from './-components/app-layout-skeleton'

// Layout for the super-admin dev playground. Each section (email templates, captured errors, UI
// components) is its OWN route under here, reached from the tab bar — the tabs are real <Link>s
// (middle-clickable, shareable, reload-stable), so a section is a bookmarkable URL rather than
// client-only state. The bare `/dev-resources` redirects to the emails tab (see dev-resources.index).
export const Route = createFileRoute('/_app/dev-resources')({
  component: RouteComponent,
  pendingComponent: PanelBlockPending,
})

const TABS = [
  { to: '/dev-resources/emails', value: 'emails', label: 'Email templates', icon: MailIcon },
  { to: '/dev-resources/errors', value: 'errors', label: 'Captured errors', icon: AlertTriangleIcon },
  { to: '/dev-resources/components', value: 'components', label: 'Components', icon: LayoutGridIcon },
] as const

function RouteComponent() {
  // Derive the active tab from the matched child route (not a pathname check) so the underline is
  // correct on reload / a direct link. The index route redirects to /emails, so that's the default.
  const errorsMatch = useMatch({ from: '/_app/dev-resources/errors', shouldThrow: false })
  const componentsMatch = useMatch({ from: '/_app/dev-resources/components', shouldThrow: false })
  const currentTab = errorsMatch ? 'errors' : componentsMatch ? 'components' : 'emails'

  return (
    <div className="flex h-full flex-col">
      <div className="px-6 pt-6 sm:px-8 sm:pt-8">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-1">
          <h1 className="text-2xl font-bold">Developer resources</h1>
          <p className="text-muted-foreground text-sm">
            Internal dev playground — preview email templates, triage captured errors, and browse UI components.
          </p>
        </div>
      </div>
      <div className="border-b">
        <div className="mx-auto w-full max-w-6xl">
          <AnimatedTabs
            initialTabId={currentTab}
            tabs={TABS.map((tab) => {
              const Icon = tab.icon
              return {
                value: tab.value,
                children: (
                  <Link to={tab.to} className="aria-[current=page]:text-foreground flex items-center gap-2 px-4 py-3">
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </Link>
                ),
              }
            })}
          />
        </div>
      </div>
      {/* data-dev-scroll: the email gallery's scroll-spy queries THIS element as its scroller (see
          email-gallery.tsx). min-h-0 lets the scroll area shrink inside the flex column so only this
          region scrolls, not the whole page (matches account.tsx). */}
      <div data-dev-scroll className="scrollbar relative min-h-0 flex-1 overflow-y-auto px-6 pb-6 sm:px-8 sm:pb-8">
        <div className="mx-auto w-full max-w-6xl pt-6 sm:pt-8">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
